/**
 * A room's static layer: the ground (an island slab outdoors, a floor and two
 * walls indoors), wall decor and flat props. Drawn into offscreen canvases and
 * blitted every frame; the town is cut into chunks so only what is on screen
 * is ever drawn or kept. Redrawn only when the zoom settles on a new scale or
 * the daylight moves on.
 */

/** A rectangle of tiles: [x0, x1) × [y0, y1). */
export interface Region { x0: number; y0: number; x1: number; y1: number }
import { doorOf, footprint, grid, groundAt, KINDS, type Grid, type Ground, type Portal, type RoomDef, type WallItem } from '../../../shared/world.ts'
import { alpha, mix, shade } from './color.ts'
import { box, floorQuad, grad, iso, line, poly, rnd, type Pt } from './iso.ts'
import { drawFlat, onFloor } from './props.ts'

type Ctx = CanvasRenderingContext2D

export const WALL_H = 2.7
const SLAB = 0.55
/** How far water sits below the ground: canals and the sea are sunk, with banks you can see. */
const DEPTH = 0.3

/** Base colour of every ground (the minimap uses these too). */
export const GROUND_COLORS: Record<Ground, string> = {
  grass: '#a5d98c', forest: '#8cc67c', moss: '#a8d18e', dirt: '#d4b483', stone: '#e4ddd0', cobble: '#cfcac1',
  gravel: '#e8e1d3', asphalt: '#5d6173', sidewalk: '#d9d5cd', planks: '#d6ae82', bridge: '#d6ccbc', tiles: '#efe7dc',
  tatami: '#dcd29c', neon: '#2b2650', sand: '#f4e0b0', wetsand: '#dcc28c', carpet: '#c9b8e8', canal: '#5aa3c8', sea: '#4f9cd0',
}
const isPit = (g: Ground) => g === 'canal' || g === 'sea' || g === 'bridge'

/** Bounds (world px) of everything the static layer can draw, for the whole room or one region. */
export function staticBounds(r: RoomDef, reg: Region = { x0: 0, y0: 0, x1: r.w, y1: r.d }): { x: number; y: number; w: number; h: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const x of [reg.x0 - 0.4, reg.x1 + 0.4]) for (const y of [reg.y0 - 0.4, reg.y1 + 0.4]) for (const z of [-SLAB - 0.3, r.outdoor ? 0.6 : WALL_H + 0.4]) {
    const p = iso(x, y, z)
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y)
  }
  return { x: Math.floor(minX) - 8, y: Math.floor(minY) - 8, w: Math.ceil(maxX - minX) + 16, h: Math.ceil(maxY - minY) + 16 }
}

export function drawStatic(ctx: Ctx, r: RoomDef, day: number, reg: Region = { x0: 0, y0: 0, x1: r.w, y1: r.d }): void {
  const g = grid(r.id)
  if (reg.x1 >= r.w || reg.y1 >= r.d) drawSlab(ctx, r, g, reg)
  // Back to front, so a pit's far banks always land over what is behind them.
  for (let y = reg.y0; y < reg.y1; y++) for (let x = reg.x0; x < reg.x1; x++) drawTile(ctx, g, x, y)
  if (!r.outdoor) {
    floorQuad(ctx, 0, 0, r.w, 1, edge(ctx, iso(0, 0), iso(r.w, 0), iso(0, 1), 'rgba(60,40,30,0.18)', 'rgba(60,40,30,0)'))
    floorQuad(ctx, 0, 0, 1, r.d, edge(ctx, iso(0, 0), iso(0, r.d), iso(1, 0), 'rgba(60,40,30,0.18)', 'rgba(60,40,30,0)'))
    drawWalls(ctx, r, day)
  }
  const near = (x: number, y: number, w: number, d: number) => x < reg.x1 && x + w > reg.x0 && y < reg.y1 && y + d > reg.y0
  for (const p of r.props) {
    if (!KINDS[p.kind].flat) continue
    const f = footprint(p)
    if (near(p.x - 0.5, p.y - 0.5, f.w + 1, f.d + 1)) drawFlat(ctx, p)
  }
  for (const p of r.portals) if (near(p.x, p.y, 1, 1)) drawPortalFloor(ctx, p)
}

/** The island's cut edge along the front sides: earth, sand, or water where the sea runs off the map. */
function drawSlab(ctx: Ctx, r: RoomDef, g: Grid, reg: Region): void {
  const W = r.w, D = r.d
  const earth = r.outdoor ? '#9c7656' : '#b39a81'
  const tone = (k: Ground) => (isPit(k) ? '#3f7fb5' : k === 'sand' || k === 'wetsand' ? '#d9b97a' : earth)
  if (reg.y1 >= D) for (let x = reg.x0; x < reg.x1; x++) {
    const k = groundAt(g, x, D - 1), top = isPit(k) ? -DEPTH : 0, c = tone(k)
    poly(ctx, [iso(x, D, top), iso(x + 1, D, top), iso(x + 1, D, -SLAB), iso(x, D, -SLAB)], grad(ctx, iso(x, D, top), iso(x, D, -SLAB), shade(c, -0.02), shade(c, -0.22)))
    if (!isPit(k) && r.outdoor) poly(ctx, [iso(x, D, 0), iso(x + 1, D, 0), iso(x + 1, D, -0.12), iso(x, D, -0.12)], shade(GROUND_COLORS[k], -0.05))
  }
  if (reg.x1 >= W) for (let y = reg.y0; y < reg.y1; y++) {
    const k = groundAt(g, W - 1, y), top = isPit(k) ? -DEPTH : 0, c = tone(k)
    poly(ctx, [iso(W, y, top), iso(W, y + 1, top), iso(W, y + 1, -SLAB), iso(W, y, -SLAB)], grad(ctx, iso(W, y, top), iso(W, y, -SLAB), shade(c, -0.2), shade(c, -0.38)))
    if (!isPit(k) && r.outdoor) poly(ctx, [iso(W, y, 0), iso(W, y + 1, 0), iso(W, y + 1, -0.12), iso(W, y, -0.12)], shade(GROUND_COLORS[k], -0.2))
  }
}

const bankOf = (k: Ground) => (k === 'sand' || k === 'wetsand' ? '#d6bc86' : k === 'grass' || k === 'forest' || k === 'moss' || k === 'dirt' ? '#9c7a5a' : '#b4ada2')

function drawTile(ctx: Ctx, g: Grid, x: number, y: number): void {
  const k = groundAt(g, x, y)
  if (isPit(k)) {
    const sea = k === 'sea' || (k === 'bridge' && [groundAt(g, x + 1, y), groundAt(g, x - 1, y), groundAt(g, x, y + 1), groundAt(g, x, y - 1)].includes('sea'))
    floorQuad(ctx, x, y, 1, 1, shade(sea ? GROUND_COLORS.sea : GROUND_COLORS.canal, (rnd(x, y, 3) - 0.5) * 0.05), -DEPTH)
    const back = groundAt(g, x, y - 1), left = groundAt(g, x - 1, y)
    if (!isPit(back)) {
      poly(ctx, [iso(x, y, -DEPTH), iso(x + 1, y, -DEPTH), iso(x + 1, y, 0), iso(x, y, 0)], shade(bankOf(back), -0.06))
      floorQuad(ctx, x, y, 1, 0.35, edge(ctx, iso(x, y, -DEPTH), iso(x + 1, y, -DEPTH), iso(x, y + 0.35, -DEPTH), 'rgba(20,40,70,0.22)', 'rgba(20,40,70,0)'), -DEPTH)
    }
    if (!isPit(left)) {
      poly(ctx, [iso(x, y, -DEPTH), iso(x, y + 1, -DEPTH), iso(x, y + 1, 0), iso(x, y, 0)], shade(bankOf(left), -0.2))
      floorQuad(ctx, x, y, 0.35, 1, edge(ctx, iso(x, y, -DEPTH), iso(x, y + 1, -DEPTH), iso(x + 0.35, y, -DEPTH), 'rgba(20,40,70,0.22)', 'rgba(20,40,70,0)'), -DEPTH)
    }
    if (k === 'bridge') drawBridge(ctx, g, x, y)
    return
  }
  const base = GROUND_COLORS[k]
  floorQuad(ctx, x, y, 1, 1, shade(base, (rnd(x, y, 7) - 0.5) * (k === 'asphalt' ? 0.03 : 0.07)))
  switch (k) {
    case 'grass': case 'forest': case 'moss': {
      ctx.strokeStyle = alpha(k === 'forest' ? '#3f7d3f' : k === 'moss' ? '#5a8f4a' : '#4f9a4a', 0.35); ctx.lineWidth = 1.2
      for (let i = 0; i < 3; i++) {
        const p = iso(x + rnd(x, y, i + 1), y + rnd(y, x, i + 4))
        ctx.beginPath(); ctx.moveTo(p.x - 2, p.y); ctx.lineTo(p.x - 3, p.y - 4); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x, p.y - 5); ctx.moveTo(p.x + 2, p.y); ctx.lineTo(p.x + 3, p.y - 3.5); ctx.stroke()
      }
      const bloom = rnd(x, y, 9)
      if (bloom < (k === 'forest' ? 0.03 : k === 'moss' ? 0.18 : 0.08)) {
        const p = iso(x + 0.5, y + 0.5)
        ctx.fillStyle = k === 'moss' ? '#ffc6d9' : rnd(x, y, 10) < 0.5 ? '#fff6f0' : '#ffe08a'
        for (let i = 0; i < 5; i++) { const a = i * 1.256; ctx.beginPath(); ctx.arc(p.x + Math.cos(a) * 2, p.y + Math.sin(a) * 1.2, 1.3, 0, Math.PI * 2); ctx.fill() }
      }
      break
    }
    case 'dirt': case 'sand': case 'wetsand': case 'asphalt':
      for (let i = 0; i < 6; i++) {
        const p = iso(x + rnd(x, y, i + 11), y + rnd(y, x, i + 17))
        ctx.fillStyle = k === 'asphalt' ? (i % 2 ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)') : i % 2 ? alpha('#ffffff', 0.3) : alpha('#8a6a3c', 0.22)
        ctx.fillRect(p.x, p.y, 1.5, 1)
      }
      break
    case 'stone':
      for (let r = 0; r < 2; r++) {
        const off = (y * 2 + r) % 2 ? 0.33 : 0
        for (let u = -off; u < 1; u += 0.66) {
          const a = Math.max(0, u), b = Math.min(1, u + 0.66)
          if (b - a > 0.05) floorQuad(ctx, x + a + 0.02, y + r * 0.5 + 0.02, b - a - 0.04, 0.46, shade(base, (rnd(Math.round((x + u) * 3), y * 2 + r, 5) - 0.5) * 0.08 - 0.01))
        }
      }
      break
    case 'cobble':
      for (let i = 0; i < 2; i++) for (let j = 0; j < 2; j++) {
        const p = iso(x + 0.25 + i * 0.5, y + 0.25 + j * 0.5)
        ctx.fillStyle = shade(base, (rnd(x * 2 + i, y * 2 + j, 3) - 0.5) * 0.12 + 0.03)
        ctx.beginPath(); ctx.ellipse(p.x, p.y, 13, 6.5, 0, 0, Math.PI * 2); ctx.fill()
      }
      break
    case 'gravel': {
      ctx.strokeStyle = 'rgba(150,140,120,0.35)'; ctx.lineWidth = 1
      for (let v = 0.2; v < 1; v += 0.3) { const a = iso(x, y + v), b = iso(x + 1, y + v); ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke() }
      break
    }
    case 'sidewalk': {
      ctx.strokeStyle = 'rgba(0,0,0,0.08)'; ctx.lineWidth = 1
      for (const v of [0.5, 1]) { line(ctx, iso(x, y + v), iso(x + 1, y + v), 'rgba(0,0,0,0.08)'); line(ctx, iso(x + v, y), iso(x + v, y + 1), 'rgba(0,0,0,0.08)') }
      break
    }
    case 'planks': {
      for (const v of [0.33, 0.66, 1]) line(ctx, iso(x, y + v), iso(x + 1, y + v), 'rgba(90,55,25,0.2)')
      const s = rnd(x, y, 2)
      line(ctx, iso(x + s, y), iso(x + s, y + 0.33), 'rgba(90,55,25,0.2)')
      line(ctx, iso(x + ((s + 0.5) % 1), y + 0.33), iso(x + ((s + 0.5) % 1), y + 0.66), 'rgba(90,55,25,0.2)')
      break
    }
    case 'tiles':
      if ((x + y) % 2) floorQuad(ctx, x, y, 1, 1, shade(base, -0.05))
      break
    case 'tatami': {
      const along = (x + y) % 2 === 0
      floorQuad(ctx, x + 0.04, y + 0.04, 0.92, 0.92, shade(base, along ? 0.03 : -0.03))
      if (along) { floorQuad(ctx, x + 0.04, y + 0.04, 0.92, 0.07, '#4a5a3a'); floorQuad(ctx, x + 0.04, y + 0.89, 0.92, 0.07, '#4a5a3a') }
      else { floorQuad(ctx, x + 0.04, y + 0.04, 0.07, 0.92, '#4a5a3a'); floorQuad(ctx, x + 0.89, y + 0.04, 0.07, 0.92, '#4a5a3a') }
      break
    }
    case 'neon':
      floorQuad(ctx, x + 0.03, y + 0.03, 0.94, 0.94, shade(base, (x + y) % 2 ? 0.04 : -0.03))
      line(ctx, iso(x, y), iso(x + 1, y), 'rgba(176,123,255,0.18)'); line(ctx, iso(x, y), iso(x, y + 1), 'rgba(176,123,255,0.18)')
      break
    case 'carpet':
      if ((x + y) % 2) floorQuad(ctx, x, y, 1, 1, shade(base, 0.03))
      break
  }
  // A stone lip along any near edge that drops into a canal.
  if (k !== 'sand' && k !== 'wetsand') {
    if (groundAt(g, x + 1, y) === 'canal') floorQuad(ctx, x + 0.86, y, 0.14, 1, '#ece6dc')
    if (groundAt(g, x, y + 1) === 'canal') floorQuad(ctx, x, y + 0.86, 1, 0.14, '#f2ece2')
  }
}

/** A stone deck over the water, railings along the water's edges, and an arch on the face you can see. */
function drawBridge(ctx: Ctx, g: Grid, x: number, y: number): void {
  const wet = (gx: number, gy: number) => { const k = groundAt(g, gx, gy); return k === 'canal' || k === 'sea' }
  const deck = '#d6ccbc'
  if (wet(x, y + 1)) {
    poly(ctx, [iso(x, y + 1, 0), iso(x + 1, y + 1, 0), iso(x + 1, y + 1, -DEPTH), iso(x, y + 1, -DEPTH)], '#bdb2a0')
    const c = iso(x + 0.5, y + 1, -DEPTH)
    ctx.save(); ctx.translate(c.x, c.y); ctx.transform(1, 0.5, 0, 1, 0, 0)
    ctx.fillStyle = '#3f6f8f'; ctx.beginPath(); ctx.ellipse(0, 0, 11, 7, 0, Math.PI, 0); ctx.fill(); ctx.restore()
  }
  if (wet(x + 1, y)) {
    poly(ctx, [iso(x + 1, y, 0), iso(x + 1, y + 1, 0), iso(x + 1, y + 1, -DEPTH), iso(x + 1, y, -DEPTH)], '#a99e8c')
    const c = iso(x + 1, y + 0.5, -DEPTH)
    ctx.save(); ctx.translate(c.x, c.y); ctx.transform(1, -0.5, 0, 1, 0, 0)
    ctx.fillStyle = '#365f7a'; ctx.beginPath(); ctx.ellipse(0, 0, 11, 7, 0, Math.PI, 0); ctx.fill(); ctx.restore()
  }
  floorQuad(ctx, x, y, 1, 1, deck)
  for (const v of [0.25, 0.5, 0.75]) line(ctx, iso(x, y + v), iso(x + 1, y + v), 'rgba(90,70,40,0.12)')
  const rail = '#efe8dc'
  if (wet(x, y - 1)) box(ctx, x, y, 0, 1, 0.12, 0.32, rail, { edge: false })
  if (wet(x - 1, y)) box(ctx, x, y, 0, 0.12, 1, 0.32, rail, { edge: false })
  if (wet(x, y + 1)) box(ctx, x, y + 0.88, 0, 1, 0.12, 0.32, rail, { edge: false })
  if (wet(x + 1, y)) box(ctx, x + 0.88, y, 0, 0.12, 1, 0.32, rail, { edge: false })
}

function edge(ctx: Ctx, a: Pt, b: Pt, c: Pt, c0: string, c1: string): CanvasGradient {
  const dx = b.x - a.x, dy = b.y - a.y
  const len = Math.hypot(dx, dy) || 1
  let nx = -dy / len, ny = dx / len
  const t = (c.x - a.x) * nx + (c.y - a.y) * ny
  if (t < 0) { nx = -nx; ny = -ny }
  const d = Math.abs(t)
  return grad(ctx, a, { x: a.x + nx * d, y: a.y + ny * d }, c0, c1)
}

// ─── Walls ────────────────────────────────────────────────────────────────

function drawWalls(ctx: Ctx, r: RoomDef, day: number): void {
  const W = r.w, D = r.d, H = WALL_H
  const wall = r.wall?.color ?? '#f4ece2'
  const side = shade(wall, -0.06)
  const trim = r.wall?.trim ?? '#c98d62'
  const RAIL = 0.95
  const dark = r.ground === 'neon'
  // Back wall (y = 0).
  poly(ctx, [iso(0, 0, 0), iso(W, 0, 0), iso(W, 0, H), iso(0, 0, H)], edge(ctx, iso(0, 0, H), iso(W, 0, H), iso(0, 0, 0), shade(wall, 0.04), shade(wall, -0.04)))
  poly(ctx, [iso(0, 0, 0), iso(W, 0, 0), iso(W, 0, RAIL), iso(0, 0, RAIL)], mix(shade(wall, -0.06), trim, 0.22))
  poly(ctx, [iso(0, 0, RAIL), iso(W, 0, RAIL), iso(W, 0, RAIL + 0.06), iso(0, 0, RAIL + 0.06)], dark ? alpha(trim, 0.9) : '#fbf7f1')
  poly(ctx, [iso(0, 0, 0), iso(W, 0, 0), iso(W, 0, 0.14), iso(0, 0, 0.14)], shade(trim, -0.05))
  // Left wall (x = 0), facing away from the key light.
  poly(ctx, [iso(0, 0, 0), iso(0, D, 0), iso(0, D, H), iso(0, 0, H)], edge(ctx, iso(0, 0, H), iso(0, D, H), iso(0, 0, 0), shade(side, 0.03), shade(side, -0.06)))
  poly(ctx, [iso(0, 0, 0), iso(0, D, 0), iso(0, D, RAIL), iso(0, 0, RAIL)], mix(shade(side, -0.07), trim, 0.22))
  poly(ctx, [iso(0, 0, RAIL), iso(0, D, RAIL), iso(0, D, RAIL + 0.06), iso(0, 0, RAIL + 0.06)], dark ? alpha(trim, 0.8) : '#f1ebe3')
  poly(ctx, [iso(0, 0, 0), iso(0, D, 0), iso(0, D, 0.14), iso(0, 0, 0.14)], shade(trim, -0.13))
  line(ctx, iso(0, 0, 0.14), iso(0, 0, H), 'rgba(60,40,30,0.12)')
  // Caps (wall thickness) and the cut ends.
  const cap = dark ? shade(wall, 0.12) : '#f9f4ee'
  poly(ctx, [iso(-0.3, -0.3, H), iso(W, -0.3, H), iso(W, 0, H), iso(0, 0, H)], cap)
  poly(ctx, [iso(-0.3, -0.3, H), iso(0, 0, H), iso(0, D, H), iso(-0.3, D, H)], cap)
  poly(ctx, [iso(-0.3, D, -SLAB), iso(0, D, -SLAB), iso(0, D, H), iso(-0.3, D, H)], grad(ctx, iso(0, D, H), iso(0, D, 0), shade(cap, -0.1), shade(cap, -0.22)))
  poly(ctx, [iso(W, -0.3, -SLAB), iso(W, 0, -SLAB), iso(W, 0, H), iso(W, -0.3, H)], grad(ctx, iso(W, 0, H), iso(W, 0, 0), shade(cap, -0.22), shade(cap, -0.34)))
  for (const item of r.walls) drawWallItem(ctx, r, item, day)
}

/** Map wall-local (u along the wall, z up) to screen. */
function wallPt(item: WallItem, u: number, z: number, out = 0.02): Pt {
  return item.wall === 'back' ? iso(u, out, z) : iso(out, u, z)
}
function wallQuad(item: WallItem, u0: number, u1: number, z0: number, z1: number, out = 0.02): Pt[] {
  return [wallPt(item, u0, z0, out), wallPt(item, u1, z0, out), wallPt(item, u1, z1, out), wallPt(item, u0, z1, out)]
}

function drawWallItem(ctx: Ctx, r: RoomDef, it: WallItem, day: number): void {
  const a = it.at, b = it.at + it.len
  const lit = it.wall === 'back' ? 0 : -0.06
  switch (it.kind) {
    case 'window': {
      const z0 = 1.15, z1 = 2.35
      poly(ctx, wallQuad(it, a - 0.06, b + 0.06, z0 - 0.06, z1 + 0.06), '#ffffff')
      const top = wallPt(it, a, z1), bot = wallPt(it, a, z0)
      const sky = ctx.createLinearGradient(top.x, top.y, bot.x, bot.y)
      sky.addColorStop(0, mix('#141a46', '#79c3ff', day)); sky.addColorStop(1, mix('#3b3570', '#dff2ff', day))
      poly(ctx, wallQuad(it, a + 0.05, b - 0.05, z0 + 0.05, z1 - 0.05), sky)
      // Hills and a tree line outside.
      ctx.save(); poly(ctx, wallQuad(it, a + 0.05, b - 0.05, z0 + 0.05, z1 - 0.05)); ctx.clip()
      for (let i = 0; i < 4; i++) {
        const u = a + (i / 3) * (b - a)
        const c = wallPt(it, u, z0 + 0.15)
        ctx.fillStyle = mix('#2a3a5a', '#8fd18a', day)
        ctx.beginPath(); ctx.arc(c.x, c.y, 14 + rnd(i, a * 10) * 8, 0, Math.PI * 2); ctx.fill()
      }
      if (day < 0.6) for (let i = 0; i < 6; i++) {
        const s = wallPt(it, a + 0.1 + rnd(i, 1) * (b - a - 0.2), z0 + 0.6 + rnd(i, 2) * 0.5)
        ctx.fillStyle = `rgba(255,255,255,${(1 - day) * 0.8})`; ctx.fillRect(s.x, s.y, 1.5, 1.5)
      }
      ctx.restore()
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.8
      const m1 = wallPt(it, (a + b) / 2, z0), m2 = wallPt(it, (a + b) / 2, z1), h1 = wallPt(it, a, (z0 + z1) / 2), h2 = wallPt(it, b, (z0 + z1) / 2)
      ctx.beginPath(); ctx.moveTo(m1.x, m1.y); ctx.lineTo(m2.x, m2.y); ctx.moveTo(h1.x, h1.y); ctx.lineTo(h2.x, h2.y); ctx.stroke()
      if (it.wall === 'back') box(ctx, a - 0.1, 0, z0 - 0.14, b - a + 0.2, 0.18, 0.08, '#ffffff', { edge: false })
      return
    }
    case 'painting': {
      const z0 = 1.3, z1 = 2.2
      poly(ctx, wallQuad(it, a, b, z0, z1, 0.04), shade('#b98559', lit))
      poly(ctx, wallQuad(it, a + 0.07, b - 0.07, z0 + 0.07, z1 - 0.07, 0.05), '#fff7ea')
      const c = it.color ?? '#ff9e6b'
      poly(ctx, [wallPt(it, a + 0.12, z0 + 0.12, 0.06), wallPt(it, a + (b - a) * 0.45, z0 + 0.6, 0.06), wallPt(it, a + (b - a) * 0.7, z0 + 0.3, 0.06), wallPt(it, b - 0.12, z0 + 0.5, 0.06), wallPt(it, b - 0.12, z0 + 0.12, 0.06)], shade(c, lit))
      const sun = wallPt(it, a + (b - a) * 0.7, z1 - 0.25, 0.06)
      ctx.fillStyle = '#ffd166'; ctx.beginPath(); ctx.arc(sun.x, sun.y, 4, 0, Math.PI * 2); ctx.fill()
      return
    }
    case 'board': {
      const z0 = 1.35, z1 = 2.35
      poly(ctx, wallQuad(it, a, b, z0, z1, 0.04), '#8a5c3c')
      poly(ctx, wallQuad(it, a + 0.07, b - 0.07, z0 + 0.07, z1 - 0.07, 0.05), '#2f3a36')
      ctx.strokeStyle = 'rgba(255,255,255,0.7)'; ctx.lineWidth = 1.2
      for (let i = 0; i < 4; i++) {
        const z = z1 - 0.38 - i * 0.14
        const p0 = wallPt(it, a + 0.25, z, 0.06), p1 = wallPt(it, a + 0.25 + (b - a - 0.5) * (0.5 + rnd(i, 5) * 0.5), z, 0.06)
        ctx.beginPath(); ctx.moveTo(p0.x, p0.y); ctx.lineTo(p1.x, p1.y); ctx.stroke()
      }
      if (it.text) wallText(ctx, it, (a + b) / 2, z1 - 0.18, it.text, '800 9px Nunito, ui-rounded, system-ui, sans-serif', '#ffffff')
      return
    }
    case 'clock': {
      const c = wallPt(it, (a + b) / 2, 2.0, 0.05)
      ctx.save(); ctx.translate(c.x, c.y); ctx.transform(1, it.wall === 'back' ? 0.5 : -0.5, 0, 1, 0, 0)
      ctx.fillStyle = '#3a3552'; ctx.beginPath(); ctx.arc(0, 0, 11, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.arc(0, 0, 9, 0, Math.PI * 2); ctx.fill()
      ctx.strokeStyle = '#3a3552'; ctx.lineWidth = 1.6; ctx.lineCap = 'round'
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -6); ctx.moveTo(0, 0); ctx.lineTo(4, 2); ctx.stroke()
      ctx.restore()
      return
    }
    case 'neon': {
      const c = it.color ?? '#ff5dd8'
      poly(ctx, wallQuad(it, a, b, 1.55, 2.25, 0.03), 'rgba(20,16,40,0.35)')
      ctx.save()
      ctx.shadowColor = c; ctx.shadowBlur = 10
      wallText(ctx, it, (a + b) / 2, 1.9, it.text ?? '', `800 ${Math.round(Math.min(16, 70 / Math.max(4, (it.text ?? '').length) * it.len))}px Nunito, ui-rounded, system-ui, sans-serif`, mix(c, '#ffffff', 0.45))
      ctx.restore()
      return
    }
    case 'mirror': {
      const z0 = 0.35, z1 = 2.4
      poly(ctx, wallQuad(it, a - 0.08, b + 0.08, z0 - 0.08, z1 + 0.1, 0.03), '#d9b45a')
      const top = wallPt(it, a, z1, 0.05), bot = wallPt(it, b, z0, 0.05)
      const g = ctx.createLinearGradient(top.x, top.y, bot.x, bot.y)
      g.addColorStop(0, '#e9f6ff'); g.addColorStop(0.5, '#bcd8ec'); g.addColorStop(1, '#f4fbff')
      poly(ctx, wallQuad(it, a, b, z0, z1, 0.05), g)
      poly(ctx, [wallPt(it, a + 0.25, z0 + 0.2, 0.06), wallPt(it, a + 0.55, z0 + 0.2, 0.06), wallPt(it, a + 1.1, z1 - 0.2, 0.06), wallPt(it, a + 0.8, z1 - 0.2, 0.06)], 'rgba(255,255,255,0.55)')
      for (const [u, z] of [[a - 0.08, z1 + 0.1], [b + 0.08, z1 + 0.1]] as const) { const q = wallPt(it, u, z, 0.06); ctx.fillStyle = '#ffd166'; ctx.beginPath(); ctx.arc(q.x, q.y, 3, 0, Math.PI * 2); ctx.fill() }
      return
    }
    case 'shoji': {
      const z0 = 0.9, z1 = 2.3
      poly(ctx, wallQuad(it, a - 0.06, b + 0.06, z0 - 0.06, z1 + 0.06, 0.03), '#5a3d2b')
      poly(ctx, wallQuad(it, a, b, z0, z1, 0.04), mix('#f6f1e4', '#ffe2a8', 1 - day))
      const n = Math.max(2, Math.round((b - a) / 0.45))
      for (let i = 1; i < n; i++) { const u = a + ((b - a) * i) / n; poly(ctx, wallQuad(it, u - 0.015, u + 0.015, z0, z1, 0.05), '#8a6a4a') }
      for (let i = 1; i < 5; i++) { const z = z0 + ((z1 - z0) * i) / 5; poly(ctx, wallQuad(it, a, b, z - 0.012, z + 0.012, 0.05), '#8a6a4a') }
      return
    }
    case 'moon': {
      // A round moon window onto a canal scene: willow, water and a far bridge.
      const c = wallPt(it, (a + b) / 2, 1.65, 0.04)
      ctx.save(); ctx.translate(c.x, c.y); ctx.transform(1, it.wall === 'back' ? 0.5 : -0.5, 0, 1, 0, 0)
      const R = (b - a) * 15
      ctx.fillStyle = '#6b3a2a'; ctx.beginPath(); ctx.arc(0, 0, R + 4, 0, Math.PI * 2); ctx.fill()
      ctx.save(); ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.clip()
      ctx.fillStyle = mix('#1d2450', '#cfe6f7', day); ctx.fillRect(-R, -R, R * 2, R * 2)
      ctx.fillStyle = mix('#28406a', '#7fbfdc', day); ctx.fillRect(-R, R * 0.3, R * 2, R)
      ctx.fillStyle = '#f4f1ea'; ctx.fillRect(-R, -R * 0.1, R * 0.9, R * 0.4); ctx.fillStyle = '#3d4250'; ctx.fillRect(-R, -R * 0.2, R * 0.95, R * 0.12)
      ctx.strokeStyle = '#efe8dc'; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(R * 0.35, R * 0.45, R * 0.35, Math.PI, 0); ctx.stroke()
      ctx.strokeStyle = '#7cbf5a'; ctx.lineWidth = 2
      for (let i = 0; i < 6; i++) { ctx.beginPath(); ctx.moveTo(R * 0.6 + i * 3, -R); ctx.quadraticCurveTo(R * 0.5 + i * 4, -R * 0.3, R * 0.55 + i * 3, R * 0.1); ctx.stroke() }
      ctx.restore(); ctx.restore()
      return
    }
    case 'scroll': {
      const z0 = 1.0, z1 = 2.3
      poly(ctx, wallQuad(it, a + 0.1, b - 0.1, z0, z1, 0.04), '#f6efe0')
      poly(ctx, wallQuad(it, a + 0.05, b - 0.05, z1, z1 + 0.06, 0.05), '#5a3d2b')
      poly(ctx, wallQuad(it, a + 0.05, b - 0.05, z0 - 0.06, z0, 0.05), '#5a3d2b')
      if (it.text) {
        const c = wallPt(it, (a + b) / 2, (z0 + z1) / 2, 0.06)
        ctx.save(); ctx.translate(c.x, c.y); ctx.transform(1, it.wall === 'back' ? 0.5 : -0.5, 0, 1, 0, 0)
        ctx.font = '700 13px "Noto Serif SC", serif'; ctx.fillStyle = '#2d2a3e'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
        const chars = [...it.text]
        chars.forEach((ch, i) => ctx.fillText(ch, 0, (i - (chars.length - 1) / 2) * 15))
        ctx.restore()
      }
      return
    }
    case 'door': {
      const z1 = 2.1
      poly(ctx, wallQuad(it, a - 0.08, b + 0.08, 0, z1 + 0.08, 0.03), '#ffffff')
      poly(ctx, wallQuad(it, a, b, 0, z1, 0.04), shade(r.ground === 'neon' ? '#4a3f86' : '#a0623e', lit))
      poly(ctx, wallQuad(it, a + 0.12, b - 0.12, 0.25, 1.0, 0.05), 'rgba(255,255,255,0.1)')
      poly(ctx, wallQuad(it, a + 0.12, b - 0.12, 1.15, 1.9, 0.05), 'rgba(255,255,255,0.1)')
      const k = wallPt(it, b - 0.18, 1.05, 0.06)
      ctx.fillStyle = '#ffd166'; ctx.beginPath(); ctx.arc(k.x, k.y, 2.2, 0, Math.PI * 2); ctx.fill()
      return
    }
  }
}

/** Text painted flat onto a wall. */
function wallText(ctx: Ctx, it: WallItem, u: number, z: number, text: string, font: string, fill: string): void {
  const c = wallPt(it, u, z, 0.06)
  ctx.save()
  ctx.translate(c.x, c.y)
  ctx.transform(1, it.wall === 'back' ? 0.5 : -0.5, 0, 1, 0, 0)
  ctx.font = font; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = fill
  ctx.fillText(text, 0, 0)
  ctx.restore()
}

// ─── Portals ──────────────────────────────────────────────────────────────

/** Indoors: a doormat inside the door. Outdoors: a worn path marker at the island's edge. */
function drawPortalFloor(ctx: Ctx, p: Portal): void {
  onFloor(ctx, 0.008, () => {
    ctx.beginPath(); ctx.roundRect(p.x + 0.1, p.y + 0.1, 0.8, 0.8, 0.12)
    ctx.fillStyle = '#a99274'; ctx.fill()
    ctx.beginPath(); ctx.roundRect(p.x + 0.18, p.y + 0.18, 0.64, 0.64, 0.08)
    ctx.fillStyle = '#b9a283'; ctx.fill()
  })
}

/** Does this exit need an arch to read as a way out (no door drawn for it)? */
export function portalNeedsArch(r: RoomDef, p: Portal): boolean {
  if (!r.outdoor) return !r.walls.some((w) => w.kind === 'door' && ((w.wall === 'back' && p.y === 0 && Math.floor(w.at) === p.x) || (w.wall === 'left' && p.x === 0 && Math.floor(w.at) === p.y)))
  return !r.props.some((b) => b.kind === 'building' && doorOf(b).x === p.x && doorOf(b).y === p.y)
}

/** A little arch over an outdoor exit, tinted with the destination's colour. */
export function drawArch(ctx: Ctx, r: RoomDef, p: Portal, color: string, t: number): void {
  // An exit through the x = 0 / x = w-1 edge is spanned along y, and vice versa.
  const acrossX = p.x === 0 || p.x === r.w - 1
  const [ax, ay, bx, by] = acrossX ? [p.x + 0.5, p.y + 0.08, p.x + 0.5, p.y + 0.92] : [p.x + 0.08, p.y + 0.5, p.x + 0.92, p.y + 0.5]
  const H = 1.75
  const post = (x: number, y: number) => box(ctx, x - 0.06, y - 0.06, 0, 0.12, 0.12, H, '#f3ead6')
  post(ax, ay)
  post(bx, by)
  const a = iso(ax, ay, H), b = iso(bx, by, H)
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 - 14 }
  ctx.strokeStyle = '#f3ead6'; ctx.lineWidth = 6; ctx.lineCap = 'round'
  ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.quadraticCurveTo(mid.x, mid.y, b.x, b.y); ctx.stroke()
  ctx.strokeStyle = color; ctx.lineWidth = 3
  ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.quadraticCurveTo(mid.x, mid.y, b.x, b.y); ctx.stroke()
  // Little lanterns and leaves along the arch.
  for (let i = 1; i < 6; i++) {
    const k = i / 6
    const x = (1 - k) * (1 - k) * a.x + 2 * (1 - k) * k * mid.x + k * k * b.x
    const y = (1 - k) * (1 - k) * a.y + 2 * (1 - k) * k * mid.y + k * k * b.y
    ctx.fillStyle = i % 2 ? '#5cc98a' : mix(color, '#ffffff', 0.3 + 0.2 * Math.sin(t * 2 + i))
    ctx.beginPath(); ctx.arc(x, y + 2, i % 2 ? 3.2 : 2.6, 0, Math.PI * 2); ctx.fill()
  }
}
